// @flow
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import AsyncSelect, {
    InputActionMeta,
    SelectProps,
    components,
    MenuComponentProps,
    type OptionType,
    ActionMeta,
    CreatableSelect,
} from '@atlaskit/select';
import { AsyncProps as ReactAsyncSelectProps } from 'react-select/async';
import { GroupTypeBase } from 'react-select';
import { debounce } from 'lodash';
import { usePrevious } from 'react-use';
import styled from 'styled-components';

export const noOptionsMessage = () => 'No matches found';

export const selectStylesBase = {
    menu: (base) => ({ ...base, zIndex: 2 }),
};

// Use when Select is inside InlineEdit/iframe where portal is clipped (e.g. issue panel).
// Menu stays in document flow so it's visible; tradeoff: opening menu pushes content down.
export const selectStylesMenuInFlow = {
    menu: (base) => ({ ...base, position: 'relative', zIndex: 2 }),
};

const MenuInfoWrapper = styled.div`
    text-align: left;
    padding: 0.5rem;
    color: #8c9bab;
`;

export const MenuWithTypeToSearchMsg = (props: MenuComponentProps) => {
    return (
        <components.Menu {...props}>
            {props.menuPlacement === 'bottom' && <MenuInfoWrapper>Start typing to find more</MenuInfoWrapper>}
            {props.children}
            {props.menuPlacement === 'top' && <MenuInfoWrapper>Start typing to find more</MenuInfoWrapper>}
        </components.Menu>
    );
};

export interface CustomAsyncSelectProps extends SelectProps, ReactAsyncSelectProps {
    creatable?: boolean;
    /** When true, menu uses position:relative so it's visible inside InlineEdit/iframe (e.g. issue panel). Avoids portal clipping. */
    menuInDocumentFlow?: boolean;
}

const CustomAsyncSelect = ({
    creatable = false,
    loadOptions,
    defaultOptions,
    onChange,
    isLoading: isLoadingFromProps,
    options: _,
    menuInDocumentFlow = false,
    ...props
}: CustomAsyncSelectProps) => {
    const [inputValue, setInputValue] = useState<string | undefined>('');
    const [options, setOptions] = useState<(OptionType | GroupTypeBase<OptionType>)[] | undefined>([]);
    const [isLoading, setIsLoading] = useState(true);

    const onInputChange = useCallback(
        (newValue: string, actionMeta: InputActionMeta) => {
            switch (actionMeta?.action) {
                case 'set-value': {
                    setIsLoading(false);
                    return inputValue;
                }
                case 'menu-close': {
                    setIsLoading(false);
                    setInputValue('');
                    return '';
                }
                default: {
                    setIsLoading(true);
                    setInputValue(newValue);
                    return newValue;
                }
            }
        },
        [inputValue],
    );

    const loadOptionsHandler = useCallback(() => {
        setIsLoading(true);
        loadOptions(inputValue)
            .then((ops) => {
                setOptions(ops);
            })
            .finally(() => setIsLoading(false));
    }, [inputValue, loadOptions]);

    const debouncedLoadOptions = useMemo(() => debounce(loadOptionsHandler, 300), [loadOptionsHandler]);

    // Stop the invocation of the debounced function after unmounting
    useEffect(() => {
        return () => {
            debouncedLoadOptions.cancel();
        };
    }, [debouncedLoadOptions]);

    const prevInputValue = usePrevious(inputValue);

    useEffect(() => {
        if (!inputValue && Array.isArray(defaultOptions)) {
            setOptions(defaultOptions);
            setIsLoading(false);
            return;
        }

        if (!inputValue && !defaultOptions) {
            setIsLoading(false);
            return;
        }

        if (loadOptions && prevInputValue !== inputValue && (defaultOptions === true || inputValue)) {
            debouncedLoadOptions();
        }
    }, [debouncedLoadOptions, defaultOptions, inputValue, loadOptions, prevInputValue]);

    const onChangeHandler = useCallback(
        (newValue: any, actionMeta: ActionMeta<any>) => {
            if (actionMeta.action === 'create-option') {
                setInputValue('');
            }
            return onChange?.(newValue, actionMeta);
        },
        [onChange],
    );

    const styles = useMemo(
        () => (menuInDocumentFlow ? selectStylesMenuInFlow : selectStylesBase),
        [menuInDocumentFlow],
    );

    const propsValues = useMemo(
        () => ({
            inputValue,
            onInputChange,
            options,
            isLoading: isLoadingFromProps || isLoading,
            components: { Menu: MenuWithTypeToSearchMsg },
            styles,
            noOptionsMessage,
            onChange: onChangeHandler,
            // When menu is in flow, don't use portal and limit menu height so InlineEdit buttons stay visible
            ...(menuInDocumentFlow ? { menuPortalTarget: null, maxMenuHeight: 200 } : {}),
            ...props,
        }),
        [
            inputValue,
            isLoading,
            isLoadingFromProps,
            menuInDocumentFlow,
            onChangeHandler,
            onInputChange,
            options,
            props,
            styles,
        ],
    );

    if (creatable) {
        return <CreatableSelect classNamePrefix="react-select" {...propsValues} />;
    }

    return <AsyncSelect classNamePrefix="react-select" {...propsValues} />;
};

export default CustomAsyncSelect;
